import React from 'react';
import { cn } from '../../utils/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarVariant = 'circle' | 'square' | 'rounded';

export interface AvatarProps {
  src?: string;
  alt?: string;
  size?: AvatarSize;
  variant?: AvatarVariant;
  fallback?: string;
  className?: string;
  children?: React.ReactNode;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = 'Avatar',
  size = 'md',
  variant = 'circle',
  fallback,
  className,
  children,
}) => {
  const [imageError, setImageError] = React.useState(false);

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };

  const variantClasses = {
    circle: 'rounded-full',
    square: 'rounded-none',
    rounded: 'rounded-lg',
  };

  const baseClasses = cn(
    'inline-flex items-center justify-center font-medium',
    sizeClasses[size],
    variantClasses[variant],
    className
  );

  React.useEffect(() => {
    setImageError(false);
  }, [src]);

  if (src && !imageError) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn(baseClasses, 'object-cover')}
        style={{
          backgroundColor: 'var(--color-neutral-200)',
          color: 'var(--color-text-primary)',
        }}
        onError={() => setImageError(true)}
        onLoad={() => setImageError(false)}
      />
    );
  }

  if (children) {
    return (
      <div
        className={baseClasses}
        style={{
          backgroundColor: 'var(--color-primary-500)',
          color: 'white',
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={baseClasses}
      style={{
        backgroundColor: 'var(--color-primary-500)',
        color: 'white',
      }}
    >
      {fallback || alt.charAt(0).toUpperCase()}
    </div>
  );
};


